<template>
  <label
    ref="labelRef"
    class="d-checkbox form-control cursor-pointer"
    :class="sizeClass"
    :disabled="disabled"
    :tabindex="disabled ? false : 0"
    @keydown.prevent.enter.space="labelRef?.click()"
  >
    <div class="flex items-center gap-2">
      <input
        v-model="internalValue"
        tabindex="-1"
        :indeterminate.prop="indeterminate"
        type="checkbox"
        :disabled="disabled"
        :required="required"
        :name="name"
        :value="nativeValue"
        :true-value="trueValue"
        :false-value="falseValue"
        @click.stop
        class="checkbox"
        :class="checkboxColorClass"
      />
      <span class="label-text">
        <slot />
      </span>
    </div>
  </label>
</template>

<script setup>
/**
 * @displayName DCheckbox
 */
import { ref, computed, watch } from "vue";

const props = defineProps({
  /**
   * Value for Vue 3 v-model
   */
  modelValue: {
    type: [String, Number, Boolean, Function, Object, Array, Symbol],
    default: undefined
  },
  
  /**
   * Value for Vue 2 backwards compatibility
   */
  value: {
    type: [String, Number, Boolean, Function, Object, Array, Symbol],
    default: undefined
  },
  
  /**
   * Name attribute of checkbox input
   */
  name: String,
  
  /**
   * Whether the checkbox is disabled
   */
  disabled: Boolean,
  
  /**
   * Whether the checkbox is required
   */
  required: Boolean,
  
  /**
   * Native value attribute of checkbox input
   */
  nativeValue: {
    type: [String, Number, Boolean, Function, Object, Array, Symbol],
    default: null,
  },
  
  /**
   * Whether the checkbox is in indeterminate state
   */
  indeterminate: Boolean,
  
  /**
   * Checkbox color (primary, secondary, accent, etc.)
   */
  color: {
    type: String,
    default: "primary",
  },
  
  /**
   * Checkbox size (xs, sm, md, lg, xl)
   */
  size: String,
  
  /**
   * Value when checkbox is checked
   */
  trueValue: {
    type: [String, Number, Boolean, Function, Object, Array, Symbol],
    default: true,
  },
  
  /**
   * Value when checkbox is unchecked
   */
  falseValue: {
    type: [String, Number, Boolean, Function, Object, Array, Symbol],
    default: false,
  },
});

// Support both Vue 2 and Vue 3 event patterns
const emit = defineEmits(["update:modelValue", "input", "change"]);
const labelRef = ref(null);

// Create a computed ref that we can use for v-model
const internalValue = computed({
  get() {
    const currentValue = props.modelValue !== undefined ? props.modelValue : props.value;
    
    // For array handling with native value (checkbox groups)
    if (Array.isArray(currentValue) && props.nativeValue !== null) {
      return currentValue.includes(props.nativeValue);
    }
    
    // For true/false values (single checkboxes)
    if (props.nativeValue === null) {
      return currentValue;
    }
    
    // For single checkboxes with custom values
    return currentValue === props.nativeValue;
  },
  set(checked) {
    const currentValue = props.modelValue !== undefined ? props.modelValue : props.value;
    let updatedValue;
    
    // Handle arrays (checkbox groups)
    if (Array.isArray(currentValue) && props.nativeValue !== null) {
      // Create a new array to avoid mutation issues
      updatedValue = [...currentValue];
      
      if (checked) {
        // Add to array if not already included
        if (!updatedValue.includes(props.nativeValue)) {
          updatedValue.push(props.nativeValue);
        }
      } else {
        // Remove from array if present
        const index = updatedValue.indexOf(props.nativeValue);
        if (index !== -1) {
          updatedValue.splice(index, 1);
        }
      }
    } 
    // Handle single checkbox with native value
    else if (props.nativeValue !== null) {
      updatedValue = checked ? props.nativeValue : props.falseValue;
    } 
    // Handle single checkbox with true/false values
    else {
      updatedValue = checked ? props.trueValue : props.falseValue;
    }

    // Emit both Vue 3 and Vue 2 events for maximum compatibility
    emit("update:modelValue", updatedValue);
    emit("input", updatedValue);
    emit("change", updatedValue);
  },
});

// Determine if checkbox should be considered "checked" based on various value conditions
const getSingleValue = () => {
  const currentValue = props.modelValue !== undefined ? props.modelValue : props.value;
  
  // Handle array values (for multiple checkboxes)
  if (Array.isArray(currentValue)) {
    return currentValue.includes(props.nativeValue);
  }

  // Handle native value comparison
  if (props.nativeValue !== null) {
    return currentValue === props.nativeValue;
  }

  // Default comparison against trueValue
  return currentValue === props.trueValue;
};

// Dynamic class binding for size
const sizeClass = computed(() => {
  return {
    "is-disabled": props.disabled,
    [`checkbox-${props.size}`]: props.size,
  };
});

// Dynamic class binding for color
const checkboxColorClass = computed(() => {
  // Convert old color format (primary-500) to new daisyUI format (checkbox-primary)
  const colorName = props.color.split("-")[0];
  return {
    [`checkbox-${colorName}`]: getSingleValue() && colorName,
  };
});
</script>
